import {
  QBItemProp,
  QBItemSelection,
  QBType,
} from '@/components/query-builder/models';

type FilterKind = 'numeric' | 'text';

const NUMERIC_OP_LABEL: Record<string, string> = {
  is: `=`,
  isnot: `≠`,
  gt: `>`,
  gte: `≥`,
  lt: `<`,
  lte: `≤`,
};

const TEXT_OP_LABEL: Record<string, string> = {
  is: `is`,
  isnot: `is not`,
  includes: `includes`,
  notincludes: `does not include`,
  regex: `matches`,
};

function getFilterKind(qbProps: QBItemProp): FilterKind {
  return qbProps.kind === QBType.NUMERIC_VALUE ? `numeric` : `text`;
}

function getOpLabel(kind: FilterKind, op: string): string {
  if (kind === `numeric`) {
    return NUMERIC_OP_LABEL[op] ?? op;
  }
  return TEXT_OP_LABEL[op] ?? op;
}

function resolveOptionLabel(qbProps: QBItemProp, rawValue: string): string {
  const allOptions = [
    ...(qbProps.options || []),
    ...(qbProps.arrayValueOptions || []),
  ];

  const match = allOptions.find(
    (o) => o.key === rawValue || o.value === rawValue,
  );

  return match?.value ?? rawValue;
}

function formatValue(
  kind: FilterKind,
  qbProps: QBItemProp,
  rawValue: string,
): string {
  const resolved = resolveOptionLabel(qbProps, rawValue);

  if (kind === `numeric`) {
    // no quotes for numeric values
    return resolved;
  }

  // basic quoting for text/enum-ish values
  return `"${resolved}"`;
}

function formatSingleCondition(item: QBItemSelection): string | null {
  const { qbProps } = item;
  const values = item.values || [];

  if (!qbProps || !values.length) return null;

  const kind = getFilterKind(qbProps);
  const label = qbProps.caption || qbProps.envKey || `Unknown`;

  const parts = values.map((v) => {
    const opLabel = getOpLabel(kind, v.op);
    const value = formatValue(kind, qbProps, String(v.value ?? ``));
    return `${label} ${opLabel} ${value}`;
  });

  if (!parts.length) return null;
  if (parts.length === 1) return parts[0];

  // OR inside the same envKey
  return `(${parts.join(` OR `)})`;
}

export function formatQueryBuilderConditions(
  queryState: QBItemSelection[],
): string {
  if (!queryState || !queryState.length) return ``;

  return queryState
    .map(formatSingleCondition)
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(` AND `);
}
